import React, {useEffect, useMemo, useState} from "react";
import {api} from "@/lib/api";
import {useAuth} from "@/auth";
import {toast} from "sonner";
import {Search, Truck, PlusCircle, Download, X, FileText} from "lucide-react";

const suffix = value => String(value || "").trim().toUpperCase().slice(-4);
const inputClass = "w-full h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm";
const emptyForm = () => ({chassis_last4:"",vehicle_id:"",custom_vehicle_name:"",vehicle_condition_good:true});

export default function VehicleManagementPage() {
  const {user} = useAuth();
  const [rows,setRows] = useState([]);
  const [pcs,setPcs] = useState([]);
  const [vehicles,setVehicles] = useState([]);
  const [search,setSearch] = useState("");
  const [pcSearch,setPcSearch] = useState("");
  const [showPCs,setShowPCs] = useState(false);
  const [adding,setAdding] = useState(false);
  const [vehicleSearch,setVehicleSearch] = useState("");
  const [form,setForm] = useState(emptyForm);
  const [selected,setSelected] = useState(null);
  const [saving,setSaving] = useState(false);
  const [downloading,setDownloading] = useState(false);
  const [matching,setMatching] = useState(null);
  const [matches,setMatches] = useState([]);
  const [matchPcId,setMatchPcId] = useState("");
  const [matchSearch,setMatchSearch] = useState("");

  const canAdd = ["admin","supervisor"].includes(user?.role) || !!user?.permissions?.add_chassis;
  const canMatch = ["admin","assistant","sales"].includes(user?.role);

  const load = async () => {
    const [ch,pc,v] = await Promise.all([
      api.get("/chassis-on-plant"), api.get("/pcs"), api.get("/vehicles")
    ]);
    setRows(ch.data || []); setPcs(pc.data || []); setVehicles(v.data || []);
  };
  useEffect(() => {load().catch(e => toast.error(e.response?.data?.detail || "Could not load vehicle management"));}, []);

  const attachedPC = row => {
    if (!row) return null;
    return pcs.find(p => p.id === row.matched_pc_id ||
      p.chassis_on_plant_id === row.id) || null;
  };
  const filtered = useMemo(() => {
    const needle=search.trim().toUpperCase();
    if (!needle) return rows;
    const chassisNeedle = /^[A-Z0-9]+$/.test(needle) && needle.length > 4 ? suffix(needle) : needle;
    return rows.filter(r =>
      String(r.chassis_last4||"").toUpperCase().includes(chassisNeedle) ||
      String(r.vehicle_name_snapshot||"").toUpperCase().includes(needle) ||
      String(r.plant_status||"").toUpperCase().includes(needle) ||
      String(attachedPC(r)?.pc_reference||attachedPC(r)?.pc_number||"").toUpperCase().includes(needle));
  },[rows,pcs,search]);
  const pcFiltered = useMemo(() => pcs.filter(p =>
    [p.pc_reference,p.pc_number,p.customer_name,p.vehicle_model,p.planned_vehicle_name,p.container_type]
      .some(v => String(v||"").toUpperCase().includes(pcSearch.trim().toUpperCase()))),[pcs,pcSearch]);

  const register = async () => {
    const value=suffix(form.chassis_last4);
    if(value.length!==4 || !/^[A-Z0-9]{4}$/.test(value)) return toast.error("Enter at least four chassis characters");
    if(!form.vehicle_id || (form.vehicle_id==="custom" && !form.custom_vehicle_name.trim())) return toast.error("Choose a vehicle type");
    setSaving(true);
    try {
      await api.post("/chassis-on-plant",{
        ...form,chassis_last4:value,
        vehicle_id:form.vehicle_id==="custom"?null:form.vehicle_id,
        custom_vehicle_name:form.vehicle_id==="custom"?form.custom_vehicle_name.trim():null,
        confirm_duplicate:false
      });
      toast.success("Chassis "+value+" registered");
      setAdding(false);setForm(emptyForm());setVehicleSearch("");
      await load();
    }catch(e){toast.error(e.response?.data?.detail || "Chassis registration failed");}
    finally{setSaving(false);}
  };

  const downloadPC = async pc => {
    if(!pc) return;
    setDownloading(true);
    try {
      const response=await api.get("/pcs/"+encodeURIComponent(pc.id)+"/pdf",{responseType:"blob"});
      const url=URL.createObjectURL(new Blob([response.data],{type:"application/pdf"}));
      const a=document.createElement("a");
      a.href=url;a.download="PC-"+String(pc.pc_reference||pc.pc_number||"document").replace(/[^A-Za-z0-9_-]/g,"-")+".pdf";
      document.body.appendChild(a);a.click();a.remove();
      setTimeout(()=>URL.revokeObjectURL(url),1000);
    }catch(e){toast.error(e.response?.data?.detail || "Could not download PC PDF");}
    finally{setDownloading(false);}
  };
  const openMatches = async row => {
    setMatching(row); setMatchPcId(""); setMatchSearch(""); setMatches([]);
    try {
      const response=await api.get("/chassis-on-plant/"+encodeURIComponent(row.id)+"/matches");
      setMatches(response.data || []);
    } catch(e) {
      toast.error(e.response?.data?.detail || "Could not find matching PCs");
    }
  };
  const matchPC = async () => {
    if(!matching || !matchPcId) return toast.error("Select a PC");
    try {
      await api.post("/chassis-on-plant/"+encodeURIComponent(matching.id)+"/match",{pc_id:matchPcId});
      toast.success("PC attached to chassis");
      setMatching(null); setMatches([]); setMatchPcId(""); setSelected(null);
      await load();
    } catch(e) {
      toast.error(e.response?.data?.detail || "PC attachment failed");
    }
  };
  const pc=attachedPC(selected);
  return <div className="space-y-5" data-testid="supervisor-vehicle-management">
    <div><h1 className="text-2xl font-semibold text-slate-900">Vehicle Management</h1>
      <p className="text-sm text-slate-500 mt-1">Search plant vehicles, inspect attached PC details and use the actions allowed for your role.</p></div>
    <div className="grid sm:grid-cols-2 gap-3">
      {canAdd&&<button type="button" onClick={()=>setAdding(v=>!v)} className="h-12 rounded-xl bg-blue-600 text-white font-semibold flex gap-2 items-center justify-center"><PlusCircle className="w-4 h-4"/>Register New Chassis</button>}
      <button type="button" onClick={()=>setShowPCs(v=>!v)} className="h-12 rounded-xl border border-blue-200 bg-white text-blue-700 font-semibold flex gap-2 items-center justify-center"><FileText className="w-4 h-4"/>{showPCs?"Hide Separate PC List":"View PCs Separately"}</button>
    </div>
    {adding&&<div className="bg-white rounded-2xl border border-blue-200 p-4 space-y-3">
      <h2 className="font-semibold">New chassis arrival</h2>
      <div className="grid md:grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-slate-600">Chassis number (last four used)
          <input value={form.chassis_last4} onChange={e=>setForm({...form,chassis_last4:suffix(e.target.value.replace(/[^a-zA-Z0-9]/g,""))})}
            placeholder="12345 becomes 2345" className={inputClass+" mt-1"} data-testid="register-chassis-input"/>
        </label>
        <label className="text-xs font-semibold text-slate-600">Vehicle type
          <input value={vehicleSearch} onChange={e=>setVehicleSearch(e.target.value)} placeholder="Filter vehicle types…" className={inputClass+" mt-1 mb-2"}/>
          <select className={inputClass} value={form.vehicle_id} onChange={e=>setForm({...form,vehicle_id:e.target.value})}>
            <option value="">Select vehicle…</option>
            {vehicles.filter(v=>String(v.name||"").toUpperCase().includes(vehicleSearch.toUpperCase())).map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
            <option value="custom">Custom Vehicle</option>
          </select>
        </label>
      </div>
      {form.vehicle_id==="custom"&&<input value={form.custom_vehicle_name} onChange={e=>setForm({...form,custom_vehicle_name:e.target.value})} placeholder="Custom vehicle name" className={inputClass}/>}
      <label className="flex gap-2 items-center text-sm"><input type="checkbox" checked={form.vehicle_condition_good} onChange={e=>setForm({...form,vehicle_condition_good:e.target.checked})}/>Vehicle condition good</label>
      <div className="flex flex-wrap gap-2"><button disabled={saving} onClick={register} className="h-10 px-5 rounded-xl bg-blue-600 text-white disabled:opacity-60">Save chassis</button>
      <button onClick={()=>setAdding(false)} className="h-10 px-5 rounded-xl border border-slate-300">Cancel</button></div>
    </div>}
    <div className="relative"><Search className="w-4 h-4 text-slate-400 absolute top-3.5 left-3"/>
      <input className={inputClass+" pl-10"} value={search} onChange={e=>setSearch(e.target.value)}
      placeholder="Search chassis, vehicle, status or attached PC…" data-testid="vehicle-management-search"/></div>
    <div className="space-y-3">
      {filtered.map(r=>{const linked=attachedPC(r);return <button type="button" key={r.id} onClick={()=>setSelected(r)}
        className="w-full text-left bg-white border border-slate-200 rounded-2xl p-4 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-300" data-testid="vehicle-record">
        <div className="font-semibold text-slate-900 flex gap-2 items-center"><Truck className="w-4 h-4 text-blue-600"/>{r.chassis_last4} · {r.vehicle_name_snapshot}</div>
        <div className="text-xs text-slate-500 mt-1">Received {r.date_received||"—"} · {r.vehicle_condition_good?"Condition good":"Condition issue"}</div>
        <div className="mt-2 text-xs font-semibold text-blue-700">{linked?"PC "+(linked.pc_reference||linked.pc_number)+" attached":"Awaiting PC attachment"} · Select to view details</div>
      </button>})}
      {!filtered.length&&<div className="text-sm text-slate-500 bg-white border rounded-xl p-5">No matching vehicles.</div>}
    </div>
    {showPCs&&<section className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
      <h2 className="font-semibold">PC records</h2>
      <input className={inputClass} value={pcSearch} onChange={e=>setPcSearch(e.target.value)} placeholder="Search PC number, customer, vehicle or container…"/>
      <div className="max-h-80 overflow-y-auto space-y-2">{pcFiltered.map(p=><div key={p.id} className="border rounded-xl p-3">
        <div className="font-semibold text-sm">{p.pc_reference||p.pc_number} · {p.customer_name||"—"}</div>
        <div className="text-xs text-slate-500">{p.vehicle_model||p.planned_vehicle_name||p.vehicle_name||"Vehicle not selected"} · {p.container_type||"Container not specified"}</div>
      </div>)}{!pcFiltered.length&&<p className="text-sm text-slate-500">No PCs found.</p>}</div>
    </section>}
    {selected&&<div className="fixed inset-0 z-50 bg-black/40 p-4 flex justify-center items-center" onClick={()=>setSelected(null)}>
      <div role="dialog" aria-modal="true" aria-label="Vehicle details" className="bg-white rounded-2xl p-5 w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-xl space-y-3" onClick={e=>e.stopPropagation()}>
        <div className="flex justify-between items-center"><h2 className="text-lg font-semibold">Vehicle details</h2><button onClick={()=>setSelected(null)} aria-label="Close details"><X className="w-5 h-5"/></button></div>
        {[
          ["Vehicle",selected.vehicle_name_snapshot],
          ["Chassis (last 4)",selected.chassis_last4],
          ["Arrival date",selected.date_received],
          ["Condition",selected.vehicle_condition_good?"Good":"Issue reported"],
          ["Plant status",selected.plant_status],
          ["PC number",pc?.pc_reference||pc?.pc_number||"Awaiting PC attachment"],
          ["Container to be mounted",pc?.container_type||pc?.container_name||pc?.product_name||"Not available"],
          ["Colour",pc?.colour||pc?.color||"Not available"]
        ].map(([label,value])=><div key={label} className="grid sm:grid-cols-2 gap-1 border-b border-slate-100 pb-2">
          <div className="text-xs font-semibold text-slate-500">{label}</div><div className="text-sm text-slate-900">{value||"—"}</div>
        </div>)}
        {pc?<button disabled={downloading} onClick={()=>downloadPC(pc)} className="w-full h-11 rounded-xl bg-blue-600 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"><Download className="w-4 h-4"/>{downloading?"Downloading…":"Download attached PC PDF"}</button>
        :canMatch?<div className="space-y-3">
          <p className="bg-amber-50 rounded-xl p-3 text-sm text-amber-900">Awaiting PC attachment.</p>
          <button type="button" onClick={()=>openMatches(selected)} className="w-full h-11 rounded-xl bg-blue-600 text-white font-semibold">Attach PC to this chassis</button>
          {matching?.id===selected.id&&<div className="border border-blue-100 rounded-xl p-3 space-y-2">
            <input className={inputClass} value={matchSearch} onChange={e=>setMatchSearch(e.target.value)} placeholder="Search PC number or customer…"/>
            <select className={inputClass} value={matchPcId} onChange={e=>setMatchPcId(e.target.value)}>
              <option value="">Select PC…</option>
              {matches.filter(p=>!matchSearch.trim()||[p.pc_reference,p.pc_number,p.customer_name,p.vehicle_model,p.planned_vehicle_name,p.vehicle_name].some(v=>String(v||"").toLowerCase().includes(matchSearch.trim().toLowerCase()))||p.id===matchPcId).map(p=><option key={p.id} value={p.id}>{p.pc_reference||p.pc_number} · {p.customer_name||"—"}</option>)}
            </select>
            <button type="button" onClick={matchPC} disabled={!matchPcId} className="w-full h-10 rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-50">Confirm PC attachment</button>
          </div>}
        </div>
        :<p className="bg-amber-50 rounded-xl p-3 text-sm text-amber-900">Awaiting PC attachment. You can view it here after an authorized Sales, Admin or Assistant user attaches the PC.</p>}
      </div>
    </div>}
  </div>;
}
